import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { account, authService } from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(account);
  useEffect(() => {
    const update = () => setUser(account());
    const refresh = () => { authService.token().then(update).catch(update); };
    const storage = event => {
      if (!event.key || event.key === 'bomwise.account') update();
    };
    window.addEventListener('bomwise-auth', update);
    window.addEventListener('storage', storage);
    window.addEventListener('focus', refresh);
    const interval = window.setInterval(refresh, 60000);
    refresh();
    return () => {
      window.removeEventListener('bomwise-auth', update);
      window.removeEventListener('storage', storage);
      window.removeEventListener('focus', refresh);
      window.clearInterval(interval);
    };
  }, []);
  return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>;
}

export function useAccount() { return useContext(AuthContext); }

export function RequireAccount({ children }) {
  const user = useAccount();
  const location = useLocation();
  useEffect(() => {
    if (!user) sessionStorage.setItem('bomwise.returnTo', location.pathname + location.search + location.hash);
  }, [user, location]);
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export function safeDestination(value) {
  return value?.startsWith('/') && !value.startsWith('//') && !value.includes('\\')
    && !/[\u0000-\u0020\u007f]/.test(value)
    && !value.startsWith('/login') && !value.startsWith('/register') ? value : '/';
}

export function AccountForm({ register = false }) {
  const [mode, setMode] = useState(register ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const navigate = useNavigate();
  const title = { login: 'Sign in', register: 'Create account', confirm: 'Verify email', forgot: 'Reset password', reset: 'Set new password' }[mode];

  useEffect(() => { setMode(register ? 'register' : 'login'); }, [register]);

  const submit = async event => {
    event.preventDefault();
    setBusy(true); setError(''); setNotice('');
    try {
      if (mode === 'register') {
        await authService.register(email, password);
        setPassword(''); setMode('confirm'); setNotice('Enter the verification code sent to your email.');
      } else if (mode === 'confirm') {
        await authService.confirm(email, code);
        setMode('login'); setCode(''); setNotice('Email verified. Sign in to continue.');
      } else if (mode === 'forgot') {
        await authService.forgot(email);
        setMode('reset'); setNotice('If an eligible account exists, a reset code has been sent.');
      } else if (mode === 'reset') {
        await authService.reset(email, code, password);
        setMode('login'); setPassword(''); setCode(''); setNotice('Password reset. Sign in to continue.');
      } else {
        await authService.login(email, password);
        const next = safeDestination(sessionStorage.getItem('bomwise.returnTo'));
        sessionStorage.removeItem('bomwise.returnTo');
        navigate(next, { replace: true });
      }
    } catch (failure) {
      if (failure.code === 'UserNotConfirmedException') setMode('confirm');
      setError(failure.message);
    } finally { setBusy(false); }
  };

  return <Card className="bomwise-auth-card mx-auto" style={{ maxWidth: 460 }}>
    <Card.Body>
      <h2>{title}</h2>
      <p className="text-muted">Use your account email to open BOMs shared with you.</p>
      {error && <Alert variant="danger">{error}</Alert>}
      {notice && <Alert variant="info">{notice}</Alert>}
      <Form onSubmit={submit}>
        <Form.Group className="mb-3" controlId="account-email">
          <Form.Label>Email</Form.Label>
          <Form.Control type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} />
        </Form.Group>
        {['login', 'register', 'reset'].includes(mode) && <Form.Group className="mb-3" controlId="account-password">
          <Form.Label>Password</Form.Label>
          <Form.Control type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            minLength={mode === 'login' ? undefined : 12} required value={password} onChange={e => setPassword(e.target.value)} />
          {mode !== 'login' && <Form.Text>At least 12 characters, including uppercase, lowercase, a number, and a symbol.</Form.Text>}
        </Form.Group>}
        {['confirm', 'reset'].includes(mode) && <Form.Group className="mb-3" controlId="account-code">
          <Form.Label>Verification code</Form.Label>
          <Form.Control autoComplete="one-time-code" required value={code} onChange={e => setCode(e.target.value)} />
        </Form.Group>}
        <Button type="submit" disabled={busy}>{busy && <Spinner size="sm" className="me-2" />}{title}</Button>
        {mode === 'confirm' && <Button variant="link" disabled={busy} onClick={async () => {
          setBusy(true); setError('');
          try { await authService.resend(email); setNotice('A new code has been sent.'); }
          catch (failure) { setError(failure.message); }
          finally { setBusy(false); }
        }}>Resend code</Button>}
      </Form>
      <div className="mt-3">
        {mode === 'login' ? <>
          <Link to="/register">Create an account</Link>
          <Button variant="link" onClick={() => setMode('forgot')}>Forgot password?</Button>
        </> : <Link to="/login" onClick={() => setMode('login')}>Back to sign in</Link>}
      </div>
    </Card.Body>
  </Card>;
}
